import { RootState } from "core-integration/src/store";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { hiDPI, size } from "polished";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";
import ESPromoSplashLeft1x from "../img/home/es-promo-splash-left.png";
import ESPromoSplashLeft2x from "../img/home/es-promo-splash-left@2x.png";
import ESPromoSplashRight1x from "../img/home/es-promo-splash-right.png";
import ESPromoSplashRight2x from "../img/home/es-promo-splash-right@2x.png";
import ESPromoLogo from "../img/logos/es-fantasy-logo.svg?react";
import TV2Login from "./auth/TV2Login";
import Copy from "./Copy";
import SubHeading from "./SubHeading";

const CopyWrap = styled(Copy)`
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.baseBold};
  color: ${({ theme }) => theme.colors.blackLighter};
  margin-top: -20px;
`;

const StyledPromoBox = styled.div`
  background-image: url(${ESPromoSplashLeft1x}), url(${ESPromoSplashRight1x});

  ${hiDPI(2)} {
    background-image: url(${ESPromoSplashLeft2x}), url(${ESPromoSplashRight2x});
  }

  background-position:
    left bottom,
    right top;
  background-repeat: no-repeat, no-repeat;
  background-size: 60%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  justify-items: center;
  gap: ${({ theme }) => theme.space[1]};
  margin: 0 1.5rem 2.5rem 1.5rem;
  padding: ${({ theme }) => theme.space[6]};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.greyDarker4};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    background-size: 40%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    grid-template-columns: auto minmax(auto, 50%) 1fr;
    gap: ${({ theme }) => `0 ${theme.space[8]}`};
    background-size: contain;
    padding: ${({ theme }) => theme.space[4]};
  }
`;

const LoginRegisterButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[5]};
  width: 100%;
  grid-column: 1;
  grid-row: 3;
  align-self: center;
  padding: ${({ theme }) => `${theme.space[4]} 0 ${theme.space[2]}`};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex-direction: row;
    flex-grow: 1;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex-direction: column;
    flex-grow: 0.48;
    grid-column: 3;
    grid-row: 1 / 3;
  }
`;

const PromoLogo = styled(ESPromoLogo)`
  ${size(40, 200)}
  grid-column: 1;
  grid-row: 1;
  align-self: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    grid-row: 1 / 3;
  }
`;

const PromoTextWrap = styled.div`
  grid-column: 1;
  grid-row: 4;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    grid-column: 2;
    grid-row: 2;
    text-align: left;
  }
`;

const PromoText = styled.p`
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 0;
`;

const StyledSubHeading = styled(SubHeading)`
  grid-column: 1;
  grid-row: 2;
  margin-bottom: 0px;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    grid-column: 2;
    grid-row: 1;
    justify-self: start;
  }
`;

const Login: React.FC = () => {
  const { t } = useTranslation();
  const player = useSelector((state: RootState) => getPlayerData(state));

  if (player && player.id) {
    return (
      <CopyWrap>
        <Copy>
          <p>
            {t(
              "login.loggedInAs",
              `You are logged in as ${player.first_name} ${player.last_name}`,
              {
                firstName: player.first_name,
                lastName: player.last_name,
              },
            )}
          </p>
        </Copy>
      </CopyWrap>
    );
  }

  return (
    <>
      <StyledPromoBox>
        <PromoLogo />
        <StyledSubHeading>
          {t("login.promo.heading", "Login with TV 2 for the 2026/27 season")}
        </StyledSubHeading>
        <PromoTextWrap>
          <PromoText>
            {t(
              "login.promo.paragraph",
              "In 2026 you must have a TV 2 account to play. Log in with your regular Fantasy email to keep your history from previous seasons.",
            )}
          </PromoText>
          <PromoText>
            {t(
              "login.promo.paragraph2",
              "If you have used social as your login previously, create a TV 2-account and use the same email both places to connect them.",
            )}
          </PromoText>
        </PromoTextWrap>
        <LoginRegisterButtonWrap>
          <TV2Login />
        </LoginRegisterButtonWrap>
      </StyledPromoBox>
    </>
  );
};

export default Login;
